/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths, and Privy form proxy functionality.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {boolean} */
    this.isSubmitting = false;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.testingMode = false;
  }

  connectedCallback() {
    // Get elements for padding adjustment
    this.button = this.querySelector(".email-signup__button--integrated");
    this.input = this.querySelector(".email-signup__input");
    this.inputGroup = this.querySelector(".email-signup__input-group");

    // Get elements for form proxy
    this.shopifyForm = this.querySelector("#shopify-email-form");
    this.emailInput = this.querySelector(".email-signup__input");
    this.privyContainer = this.querySelector(".privy-embed-hidden");

    const isTextButton =
      this.button &&
      this.button.classList.contains("email-signup__button--text");

    // Handle padding adjustment for integrated buttons
    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ("fonts" in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    // Set up Privy proxy functionality
    if (this.shopifyForm && this.privyContainer) {
      this.shopifyForm.addEventListener("submit", this.handleFormSubmit);

      // Add to window for testing
      window.emailSignupBlock = this;
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.shopifyForm) {
      this.shopifyForm.removeEventListener("submit", this.handleFormSubmit);
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = this.button.offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      this.inputGroup.style.setProperty(
        "--button-actual-width",
        `${buttonWidth}px`,
      );
    }
  }

  /**
   * Handle Shopify form submission and proxy to Privy
   * @param {Event} event
   */
  async handleFormSubmit(event) {
    console.log("Form submitted, checking conditions...");

    if (this.isSubmitting || !this.emailInput || !this.privyContainer) {
      console.log("Submission blocked:", {
        isSubmitting: this.isSubmitting,
        hasEmailInput: !!this.emailInput,
        hasPrivyContainer: !!this.privyContainer,
      });
      return;
    }

    const email = this.emailInput.value.trim();
    console.log("Email to submit:", email);

    // Validate email
    if (!email || !this.isValidEmail(email)) {
      console.log("Invalid email, letting default validation handle");
      return;
    }

    // Prevent default Shopify submission and proxy to Privy
    console.log("Preventing default form submission and proxying to Privy");
    event.preventDefault();
    event.stopPropagation();

    this.isSubmitting = true;
    this.setLoadingState(true);

    try {
      console.log("Attempting Privy submission...");
      await this.submitToPrivy(email);
      console.log("Privy submission successful");
      this.showSuccessMessage();
    } catch (error) {
      console.error("Privy submission failed:", error);
      this.showErrorMessage("Something went wrong. Please try again.");
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }

  /**
   * Enable testing mode - shows Privy form temporarily
   */
  enableTestingMode() {
    this.testingMode = true;
    console.log(
      "Testing mode enabled - Privy form will be visible during submission",
    );
  }

  /**
   * Disable testing mode
   */
  disableTestingMode() {
    this.testingMode = false;
    console.log("Testing mode disabled");
  }

  /**
   * Submit email to Privy using the embedded form
   * @param {string} email
   */
  async submitToPrivy(email) {
    console.log("Looking for Privy iframe...");

    // Find the Privy form in the hidden container
    const privyIframe = this.privyContainer?.querySelector("iframe");

    if (!privyIframe) {
      console.error("Privy iframe not found in container");
      throw new Error("Privy iframe not found");
    }

    console.log("Found Privy iframe, waiting for load...");

    // Show form temporarily if in testing mode
    if (this.testingMode) {
      this.showPrivyFormForTesting();
    }

    // Wait for iframe to load if needed
    await this.waitForIframeLoad(privyIframe);

    try {
      console.log("Attempting to access iframe content...");

      // Access the iframe document
      const iframeDoc =
        privyIframe.contentDocument || privyIframe.contentWindow?.document;

      if (!iframeDoc) {
        console.log("Cannot access iframe due to CORS, using fallback");
        throw new Error("Cannot access Privy iframe - CORS restriction");
      }

      console.log("Successfully accessed iframe content");

      // Find and fill the email input
      const emailInput = iframeDoc.querySelector(
        'input[type="email"], input[name*="email"]',
      );
      if (!emailInput) {
        console.error("Email input not found in Privy form");
        throw new Error("Email input not found in Privy form");
      }

      console.log("Found email input, filling with:", email);

      // Fill the email
      emailInput.value = email;
      emailInput.dispatchEvent(new Event("input", { bubbles: true }));
      emailInput.dispatchEvent(new Event("change", { bubbles: true }));

      // Find and click the submit button
      const submitButton = iframeDoc.querySelector(
        'button[type="submit"], #privy-submit-btn, .privy-button-element',
      );
      if (!submitButton) {
        console.error("Submit button not found in Privy form");
        throw new Error("Submit button not found in Privy form");
      }

      console.log("Found submit button, clicking...");

      // Submit the form
      submitButton.click();

      console.log("Clicked submit button successfully");
    } catch (error) {
      console.log("Iframe method failed, trying fallback:", error.message);
      // If we can't access iframe content due to CORS, fallback to creating our own form
      this.submitViaExternalForm(email);
    }
  }

  /**
   * Wait for iframe to fully load
   * @param {HTMLIFrameElement} iframe
   */
  waitForIframeLoad(iframe) {
    return new Promise((resolve, reject) => {
      console.log("Waiting for iframe to load...");

      const timeout = setTimeout(() => {
        console.log("Iframe load timeout");
        reject(new Error("Iframe load timeout"));
      }, 5000);

      if (
        iframe.contentDocument &&
        iframe.contentDocument.readyState === "complete"
      ) {
        console.log("Iframe already loaded");
        clearTimeout(timeout);
        resolve(true);
      } else {
        iframe.onload = () => {
          console.log("Iframe loaded successfully");
          clearTimeout(timeout);
          resolve(true);
        };
        iframe.onerror = () => {
          console.log("Iframe failed to load");
          clearTimeout(timeout);
          reject(new Error("Iframe failed to load"));
        };
      }
    });
  }

  /**
   * Submit via external form as fallback when CORS blocks iframe access
   * @param {string} email
   */
  submitViaExternalForm(email) {
    console.log("Using external form fallback method");

    const campaignId = this.extractCampaignId();
    if (!campaignId) {
      console.error("Cannot extract campaign ID for fallback submission");
      return;
    }

    console.log("Creating external form with campaign ID:", campaignId);

    const form = document.createElement("form");
    form.method = "POST";
    form.action = `https://promotions.lpage.co/campaigns/${campaignId}/form`;
    form.style.display = "none";
    form.target = "privy_submit_" + Date.now(); // Use unique target name

    const emailInput = document.createElement("input");
    emailInput.type = "hidden";
    emailInput.name = "customer_attributes[email]";
    emailInput.value = email;

    const utf8Input = document.createElement("input");
    utf8Input.type = "hidden";
    utf8Input.name = "utf8";
    utf8Input.value = "✓";

    form.appendChild(emailInput);
    form.appendChild(utf8Input);
    document.body.appendChild(form);

    console.log("Submitting external form to:", form.action);
    form.submit();

    console.log("External form submitted");

    // Clean up
    setTimeout(() => {
      if (document.body.contains(form)) {
        document.body.removeChild(form);
      }
    }, 1000);
  }

  /**
   * Show Privy form temporarily for testing
   */
  showPrivyFormForTesting() {
    if (!this.privyContainer) return;

    console.log("Showing Privy form for testing...");

    // Make container visible
    this.privyContainer.style.cssText = `
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
      width: 500px !important;
      height: 400px !important;
      z-index: 10000 !important;
      background: white !important;
      border: 2px solid #333 !important;
      border-radius: 8px !important;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3) !important;
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: auto !important;
    `;

    // Add backdrop
    const backdrop = document.createElement("div");
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      z-index: 9999;
    `;
    backdrop.id = "privy-test-backdrop";

    // Close on backdrop click
    backdrop.onclick = () => {
      this.hidePrivyFormForTesting();
    };

    document.body.appendChild(backdrop);

    // Auto-hide after 10 seconds
    setTimeout(() => {
      this.hidePrivyFormForTesting();
    }, 10000);
  }

  /**
   * Hide Privy form after testing
   */
  hidePrivyFormForTesting() {
    if (!this.privyContainer) return;

    console.log("Hiding Privy form after testing");

    // Reset container styles
    this.privyContainer.style.cssText = `
      position: absolute !important;
      left: -9999px !important;
      top: -9999px !important;
      width: 1px !important;
      height: 1px !important;
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      z-index: -1 !important;
    `;

    // Remove backdrop
    const backdrop = document.getElementById("privy-test-backdrop");
    if (backdrop) {
      backdrop.remove();
    }
  }

  /**
   * Show captcha message
   * @param {HTMLIFrameElement} iframe
   */
  showCaptchaMessage(iframe) {
    // Create close button
    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = "×";
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 15px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      z-index: 10001;
      color: #666;
    `;

    closeBtn.onclick = () => {
      iframe.parentElement.remove();
      this.setLoadingState(false);
      this.isSubmitting = false;
    };

    iframe.parentElement.appendChild(closeBtn);

    // Create instruction message
    const message = document.createElement("div");
    message.innerHTML = "Complete the form below to sign up:";
    message.style.cssText = `
      position: absolute;
      top: -40px;
      left: 0;
      right: 0;
      text-align: center;
      background: white;
      padding: 10px;
      border-radius: 4px 4px 0 0;
      font-size: 14px;
      color: #333;
    `;

    iframe.parentElement.appendChild(message);
  }

  /**
   * Extract campaign ID from Privy embed code
   */
  extractCampaignId() {
    if (!this.privyContainer) return null;

    const match = this.privyContainer.innerHTML.match(/data-campaign="(\d+)"/);
    return match ? match[1] : null;
  }

  /**
   * Validate email format
   * @param {string} email
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Set loading state on the form
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    const submitButton = this.querySelector(".email-signup__button");
    if (!submitButton) return;

    if (isLoading) {
      submitButton.disabled = true;
      submitButton.setAttribute("data-loading", "true");

      // Store original text for text buttons
      const originalText = submitButton.textContent;
      if (originalText && !submitButton.hasAttribute("data-original-text")) {
        submitButton.setAttribute("data-original-text", originalText.trim());
      }

      // Add loading class for additional styling
      submitButton.classList.add("email-signup__button--loading");
    } else {
      submitButton.disabled = false;
      submitButton.removeAttribute("data-loading");
      submitButton.classList.remove("email-signup__button--loading");

      // Restore original button text
      const originalText = submitButton.getAttribute("data-original-text");
      if (originalText) {
        submitButton.textContent = originalText;
        submitButton.removeAttribute("data-original-text");
      }
    }
  }

  /**
   * Show success message
   */
  showSuccessMessage() {
    this.hideMessages();

    let successMessage = this.querySelector(".email-signup__message--success");
    if (!successMessage) {
      successMessage = document.createElement("div");
      successMessage.className =
        "email-signup__message email-signup__message--success";
      successMessage.innerHTML = `
        <span class="svg-wrapper icon-success">
          <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
            <path d="M7 10l2 2 4-4" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <p class="email-signup__message-text">Thanks for signing up! You'll hear from us soon.</p>
      `;
      this.shopifyForm?.appendChild(successMessage);
    }

    successMessage.style.display = "flex";
    successMessage.setAttribute("tabindex", "-1");
    successMessage.focus();

    // Clear the input
    if (this.emailInput) {
      this.emailInput.value = "";
    }
  }

  /**
   * Show error message
   * @param {string} message
   */
  showErrorMessage(message) {
    this.hideMessages();

    let errorMessage = this.querySelector(".email-signup__message--error");
    if (!errorMessage) {
      errorMessage = document.createElement("div");
      errorMessage.className =
        "email-signup__message email-signup__message--error";
      this.shopifyForm?.appendChild(errorMessage);
    }

    errorMessage.innerHTML = `
      <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" class="icon-error">
        <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm5 13.59L13.59 15 10 11.41 6.41 15 5 13.59 8.59 10 5 6.41 6.41 5 10 8.59 13.59 5 15 6.41 11.41 10 15 13.59z" fill="currentColor"/>
      </svg>
      <span>${message}</span>
    `;

    errorMessage.style.display = "flex";
    errorMessage.setAttribute("tabindex", "-1");
    errorMessage.focus();
  }

  /**
   * Hide all messages
   */
  hideMessages() {
    const messages = this.querySelectorAll(".email-signup__message");
    messages.forEach((message) => {
      message.style.display = "none";
    });
  }
}

if (!customElements.get("email-signup-block")) {
  customElements.define("email-signup-block", EmailSignupBlock);
}
